import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "@/config/firebase";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB before compression
const TARGET_SIZE_BYTES = 200 * 1024; // 200 KB target after compression
const MAX_DIMENSION = 512; // px
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export class ImageUploadError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ImageUploadError";
    }
}

export function validateImageFile(file: File): void {
    if (!ACCEPTED_TYPES.includes(file.type)) {
        throw new ImageUploadError(
            "Invalid file type. Please upload a JPG, PNG, GIF, or WebP image."
        );
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new ImageUploadError(
            `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 5 MB.`
        );
    }
}

export async function compressImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let { width, height } = img;
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new ImageUploadError("Canvas not supported"));
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);

            const isPNG = file.type === "image/png";

            if (isPNG) {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new ImageUploadError("Compression failed"));
                            return;
                        }
                        resolve(blob);
                    },
                    "image/png"
                );
            } else {
                let quality = 0.8;
                const tryCompress = () => {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new ImageUploadError("Compression failed"));
                                return;
                            }
                            if (blob.size > TARGET_SIZE_BYTES && quality > 0.3) {
                                quality -= 0.1;
                                tryCompress();
                            } else {
                                resolve(blob);
                            }
                        },
                        "image/jpeg",
                        quality
                    );
                };
                tryCompress();
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new ImageUploadError("Failed to read image file"));
        };

        img.src = url;
    });
}

export async function uploadToFirebaseStorage(
    blob: Blob,
    originalFilename: string
): Promise<string> {
    const sanitized = originalFilename
        .replace(/[^a-zA-Z0-9._-]/g, "_")
        .substring(0, 50);
    const extension = blob.type === "image/png" ? ".png" : ".jpg";
    const storagePath = `cabal-images/${Date.now()}_${sanitized}${extension}`;

    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, blob, { contentType: blob.type });

    return getDownloadURL(storageRef);
}

export async function processAndUploadImage(file: File): Promise<string> {
    validateImageFile(file);
    const compressed = await compressImage(file);
    return uploadToFirebaseStorage(compressed, file.name);
}
